/**
 * WorkWise content of step 4 (Application (ERP)): version, tenant, license key
 * reference, SMTP and the read-only DB connection reference. Credentials are
 * Key Vault references only. Only NewDeploymentPage.module.css classes.
 *
 * The first toggle skips the whole step (infrastructure only): the fields below it are then neither
 * shown, validated nor sent, and turning it off brings them back with whatever was typed.
 */
import { FormProvider, useFormContext, useWatch } from 'react-hook-form'
import type { WorkwiseFlow } from '@/hooks/useWorkwiseFlow'
import styles from '@/pages/NewDeploymentPage.module.css'
import type { WorkwiseFormValues } from '@/types/workwise'
import { APPLICATION_STEP } from '@/utils/workwise/serverErrors'
import { InfoField, Section, SelectField, TextField, ToggleField } from './fields'
import { StepServerErrors } from './WorkwiseInfra'

function ApplicationSections({ flow }: { flow: WorkwiseFlow }) {
  const { control } = useFormContext<WorkwiseFormValues>()
  const dbConnectionRef = useWatch({ control, name: 'app.dbConnectionRef' })
  const versionOptions = (flow.versions ?? []).map((v) => ({
    value: v.version,
    label: v.is_default ? `${v.version} (default)` : v.version,
  }))
  return (
    <>
      <Section title="WorkWise Application" testId="ww-app-settings">
        <div className={styles.formGrid}>
          {versionOptions.length > 0 ? (
            <SelectField path="app.version" label="WorkWise Version" options={versionOptions} required />
          ) : (
            <TextField
              path="app.version"
              label="WorkWise Version"
              required
              hint={flow.versionsLoading ? 'Loading versions…' : 'The version list is unavailable; type a version.'}
            />
          )}
          <TextField path="app.tenantName" label="Tenant Name" required />
          <TextField path="app.licenseKeyRef" label="License Key (Key Vault reference)" placeholder="kv://my-vault/workwise-license" required />
          <InfoField
            label="DB Connection (Key Vault reference)"
            value={dbConnectionRef || 'kv://<vault>/ww-<nnn>-<env>-db-connection'}
            hint="Auto-computed from the customer number and environment"
            testId="ww-db-connection-ref"
          />
        </div>
      </Section>
      <Section title="E-mail (SMTP)" testId="ww-smtp">
        <div className={styles.formGrid}>
          <TextField path="app.smtp.host" label="SMTP Host" placeholder="smtp.example.com" required />
          <TextField path="app.smtp.port" label="SMTP Port" type="number" number min={1} max={65535} required />
          <TextField path="app.smtp.username" label="SMTP Username" hint="Leave empty for an unauthenticated relay" />
          <TextField path="app.smtp.passwordRef" label="SMTP Password (Key Vault reference)" placeholder="kv://my-vault/smtp-password" required />
          <TextField path="app.smtp.fromAddress" label="From Address" placeholder="noreply@example.com" inputMode="email" required />
        </div>
        <div style={{ marginTop: 16 }}>
          <ToggleField path="app.smtp.useTls" label="Use TLS" desc="Connect to the SMTP server over TLS" />
        </div>
      </Section>
    </>
  )
}

function AppSettingsFields({ flow }: { flow: WorkwiseFlow }) {
  return (
    <div className={styles.form}>
      <StepServerErrors flow={flow} step={APPLICATION_STEP} />
      <Section title="Application setup" testId="ww-app-skip">
        <ToggleField
          path="skipApplication"
          label="Skip application settings (deploy the infrastructure only)"
          desc="The servers are created without the settings below and the application is set up later. Turn this off to fill them in."
        />
      </Section>
      {flow.skipApplication ? (
        <p className={styles.stepDesc} data-testid="ww-app-skipped">
          The application settings are skipped, so nothing here is validated or sent. Anything you typed is kept if you turn the option off.
        </p>
      ) : (
        <ApplicationSections flow={flow} />
      )}
    </div>
  )
}

/** Step 4 for WorkWise, in place of the "no application-level configuration" text. */
export function WorkwiseAppSettings({ flow }: { flow: WorkwiseFlow }) {
  return (
    <FormProvider {...flow.form}>
      <AppSettingsFields flow={flow} />
    </FormProvider>
  )
}
